import { markdown } from './markdown';
import {
  cmsGet, el, fmtDate, guideTile, loadCSS, mediaUrl, newsTile, renderFooter, siteHeader, unloadCSS,
  wirePlayLinks, type GuideItem, type NewsItem, type SitePage,
} from './site-shell';

/**
 * News and Guides — `/news`, `/news/<slug>`, `/guides`, `/guides/<slug>`.
 *
 * Ordinary SPA routes with the landing's own header and footer. The text is the
 * CMS's (GET /api/news, /api/guides), edited in the admin panel; nothing here is
 * authored content.
 */

type Section = Exclude<SitePage, 'home'>;

const COPY: Record<Section, {
  heading: string; eyebrow: string; lead: string;
  back: string; missing: string; empty: string; failed: string;
}> = {
  news: {
    heading: 'News',
    eyebrow: 'From the valley',
    lead: 'Patch notes, events and what we are building next.',
    back: 'All news',
    missing: 'That article is not available. Head back to the news list.',
    empty: 'No news yet — the first dispatch from the valley is on its way.',
    failed: 'News is taking a moment to load. Try again shortly.',
  },
  guides: {
    heading: 'Guides',
    eyebrow: 'Learn the valley',
    lead: 'How the systems actually work, written by people who play it.',
    back: 'All guides',
    missing: 'That guide is not available. Head back to the guide list.',
    empty: 'Guides are being written. Check back soon.',
    failed: 'Guides are taking a moment to load. Try again shortly.',
  },
};

/** `/news` → { section: 'news', slug: null }; `/guides/x` → { 'guides', 'x' }; else null. */
export function parseArticleRoute(path: string): { section: Section; slug: string | null } | null {
  const m = /^\/(news|guides)(?:\/([^/]+))?\/?$/.exec(path);
  if (!m) return null;
  let slug: string | null = null;
  if (m[2]) {
    try { slug = decodeURIComponent(m[2]); } catch { slug = m[2]; }
  }
  return { section: m[1] as Section, slug };
}

/**
 * The old static pages, `/site/news.html?slug=x` and friends, as their new route.
 * Links to them are out in the world — shared posts, bookmarks, search results —
 * so they keep working. Null for any other path.
 */
export function legacyArticleRedirect(path: string, search: string): string | null {
  const m = /^\/site\/(news|guides)\.html$/.exec(path);
  if (!m) return null;
  const slug = new URLSearchParams(search).get('slug');
  return '/' + m[1] + (slug ? '/' + encodeURIComponent(slug) : '');
}

function notice(mount: HTMLElement, text: string) {
  mount.replaceChildren(el('div', 'notice', text));
}

function renderArticle(mount: HTMLElement, item: NewsItem & GuideItem, section: Section) {
  const back = el('a', 'back-link', '‹ ' + COPY[section].back) as HTMLAnchorElement;
  back.href = '/' + section;

  const h1 = el('h1', null, item.title);
  h1.style.marginTop = '10px';

  const meta = el('div', 'tile-meta');
  meta.style.marginTop = '10px';
  if (item.tag) meta.appendChild(el('span', 'pill', item.tag));
  if (item.category) meta.appendChild(el('span', 'pill', item.category));
  const when = fmtDate(item.publishedAt);
  if (when) meta.appendChild(el('span', null, when));

  const parts: HTMLElement[] = [back, h1, meta];
  if (item.imageUrl) {
    const img = new Image();
    img.className = 'hero-shot';
    img.src = mediaUrl(item.imageUrl);
    img.alt = '';
    parts.push(img);
  }
  if (item.summary) {
    const lead = el('p', 'lead', item.summary);
    lead.style.marginTop = '20px';
    parts.push(lead);
  }
  const prose = el('div', 'prose');
  prose.innerHTML = markdown(item.content);
  parts.push(prose);

  mount.replaceChildren(...parts);
}

/** Category tabs over the guide tiles — shown once there is more than one category. */
function categoryTabs(categories: string[], tiles: HTMLElement): HTMLElement {
  const tabs = el('div', 'tabs');
  tabs.setAttribute('role', 'tablist');
  for (const c of ['all', ...categories]) {
    const b = el('button', 'tab', c === 'all' ? 'All' : c) as HTMLButtonElement;
    b.type = 'button';
    b.setAttribute('role', 'tab');
    b.setAttribute('aria-selected', String(c === 'all'));
    b.addEventListener('click', () => {
      tabs.querySelectorAll('.tab').forEach((t) => t.setAttribute('aria-selected', String(t === b)));
      tiles.querySelectorAll<HTMLElement>('.tile').forEach((t) => {
        t.style.display = c === 'all' || t.getAttribute('data-category') === c ? '' : 'none';
      });
    });
    tabs.appendChild(b);
  }
  return tabs;
}

function loadList(mount: HTMLElement, section: Section, isLive: () => boolean) {
  const copy = COPY[section];
  const req = section === 'news'
    ? cmsGet<{ items?: NewsItem[] }>('/news?limit=30')
    : cmsGet<{ items?: GuideItem[]; categories?: string[] }>('/guides');
  req.then((data) => {
    if (!isLive()) return;
    const items = data.items || [];
    if (!items.length) { notice(mount, copy.empty); return; }
    const tiles = el('div', 'tiles');
    for (const i of items) tiles.appendChild(section === 'news' ? newsTile(i) : guideTile(i));
    const cats = 'categories' in data ? data.categories || [] : [];
    mount.replaceChildren(...(cats.length > 1 ? [categoryTabs(cats, tiles)] : []), tiles);
  }).catch(() => { if (isLive()) notice(mount, copy.failed); });
}

function loadOne(mount: HTMLElement, section: Section, slug: string, isLive: () => boolean) {
  cmsGet<NewsItem & GuideItem>(`/${section}/${encodeURIComponent(slug)}`).then((item) => {
    if (!isLive()) return;
    renderArticle(mount, item, section);
    document.title = `${item.title} — Terraboma ${section === 'news' ? 'news' : 'guides'}`;
  }).catch(() => { if (isLive()) notice(mount, COPY[section].missing); });
}

export function renderArticlesPage(
  container: HTMLElement,
  navigate: (path: string) => void,
  section: Section,
  slug: string | null,
): () => void {
  const copy = COPY[section];
  const prevTitle = document.title;
  document.title = `${copy.heading} — Terraboma`;

  loadCSS();
  // Document-level scrolling, as on the landing — see the data-landing block in app.css.
  document.documentElement.setAttribute('data-landing', 'on');

  // The single-article view drops the big page heading: the article's own title
  // is the h1 there, and the back link sits right under the header.
  const head = slug ? '' : `
    <section class="page-head">
      <div class="wrap">
        <p class="eyebrow">${copy.eyebrow}</p>
        <h1 style="font-size:clamp(28px,5vw,46px)">${copy.heading}</h1>
        <p class="lead">${copy.lead}</p>
      </div>
    </section>`;

  container.innerHTML = `${siteHeader(section)}
  <main id="main">${head}
    <section style="padding-top:${slug ? 'clamp(24px,4vw,44px)' : '8px'}">
      <div class="wrap">
        <div id="articles-root"><div class="notice">Loading…</div></div>
      </div>
    </section>
  </main>
  <footer id="site-footer"></footer>`;

  wirePlayLinks(container, navigate);
  renderFooter(container, section);
  // A new page starts at its top, not wherever the previous page was scrolled to.
  window.scrollTo(0, 0);

  // A reply that lands after the reader has moved on must not paint into the
  // next page's container.
  let live = true;
  const isLive = () => live;
  const mount = container.querySelector<HTMLElement>('#articles-root')!;
  if (slug) loadOne(mount, section, slug, isLive);
  else loadList(mount, section, isLive);

  return () => {
    live = false;
    document.title = prevTitle;
    unloadCSS();
    document.documentElement.removeAttribute('data-landing');
    container.innerHTML = '';
  };
}
